import { EFinancingProgram } from '@if/core/types'
import { formatDateTime } from '@if/core/utils'
import { ActionButton, Icon, useConfirmDialog } from '@if/mui'
import { Stack, Tooltip } from '@mui/material'
import { Clock } from 'lucide-react'
import { useSendUploadDocumentRequest } from '@src/data/api/credit-api/upload-document-request-api'
import { Dispatch, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  lastSentOn: Date | null
  setError: Dispatch<Error>
}

const SendUploadRequestButton = ({ creditApplicationId, financingProgramId, lastSentOn, setError }: Props) => {
  const { t } = useTranslation()

  const [sendUploadDocumentRequest, isPending] = useSendUploadDocumentRequest(creditApplicationId, financingProgramId)

  const [sentOn, setSentOn] = useState<Date | null>(lastSentOn ? new Date(lastSentOn) : null)

  useEffect(() => {
    setSentOn(lastSentOn ? new Date(lastSentOn) : null)
  }, [lastSentOn])

  const confirmSend = useConfirmDialog({
    title: t('documentSectionHeaders.sendUploadRequestConfirmTitle'),
    message: t('documentSectionHeaders.sendUploadRequestConfirmMessage'),
    onConfirm: async () => {
      try {
        const result = await sendUploadDocumentRequest()
        setSentOn(result.documentUploadRequestSentOn ? new Date(result.documentUploadRequestSentOn) : null)
      } catch (e) {
        setError(e as Error)
      }
    },
  })

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {sentOn && (
        <Tooltip title={t('documentSectionHeaders.lastSentOn', { date: formatDateTime(sentOn) })} placement="top">
          <span>
            <Icon icon={Clock} size="sm" color="action" />
          </span>
        </Tooltip>
      )}
      <ActionButton loading={isPending} onClick={() => void confirmSend.open()}>
        {t('documentSectionHeaders.documentRequest')}
      </ActionButton>
    </Stack>
  )
}

export default SendUploadRequestButton
